import {ProductDataStore} from "../stores/productDataStore";
import {ShapeInterface} from "../components/drawingComponent/interface/shapeInterface";
import {ProductDataService} from "./productDataService";

export class ShapeService {

    private productDataStore: ProductDataStore;
    private productDataService: ProductDataService;
    private shape: ShapeInterface;

    constructor() {
        this.productDataStore = new ProductDataStore();
        this.productDataService = new ProductDataService();
    }

    setShapeObject()
    {
        const newWidth = this.productDataStore.getAttributeByName('width');
        const newHeight = this.productDataStore.getAttributeByName('height');
        let oldHeight, oldWidth;
        const h1 = this.productDataStore.getProductShapeDimension('h1');
        const h2 = this.productDataStore.getProductShapeDimension('h2');
        const h3 = this.productDataStore.getProductShapeDimension('h3');
        const s1 = this.productDataStore.getProductShapeDimension('s1');
        const s2 = this.productDataStore.getProductShapeDimension('s2');
        const s3 = this.productDataStore.getProductShapeDimension('s3');
        switch (this.productDataStore.getProductShape()) {
            case 'triangle':
            case 'polygon':
                oldWidth = s1 + s2 + s3 === 0 ? newWidth : s1 + s2 + s3;
                oldHeight = h1 === 0 ? newHeight : h1;
                this.productDataStore.setProductShapeDimension('h1', Math.round(newHeight));
                this.productDataStore.setProductShapeDimension('h2', Math.round((newHeight * h2) / oldHeight));
                this.productDataStore.setProductShapeDimension('h3', Math.round((newHeight * h3) / oldHeight));
                this.productDataStore.setProductShapeDimension('s1', Math.round((newWidth * s1) / oldWidth));
                this.productDataStore.setProductShapeDimension('s2', Math.round((newWidth * s2) / oldWidth));
                this.productDataStore.setProductShapeDimension('s3', Math.round((newWidth * s3) / oldWidth));
                break;
            case 'arc':
                oldHeight = h1 + h2;
                this.productDataStore.setProductShapeDimension('h1', Math.round(newHeight * h1 / oldHeight));
                this.productDataStore.setProductShapeDimension('h2', Math.round(newHeight * h2 / oldHeight));
                this.productDataStore.setProductShapeDimension('s1', Math.round(newWidth));
                break;
            case 'circle':
                this.productDataStore.setProductShapeDimension('h1', Math.round(newWidth));
                this.productDataStore.setProductShapeDimension('d', Math.round(newWidth));
                this.productDataStore.setProductShapeDimension('s1', Math.round(newWidth));
                break;
            case 'rectangle':
                this.productDataStore.setProductShapeDimension('h1', Math.round(newHeight));
                this.productDataStore.setProductShapeDimension('h3', Math.round(newHeight));
                this.productDataStore.setProductShapeDimension('s1', Math.round(newWidth));
                break;
        }
    }

    setDimension(dimensionName: string, dimensionValue: number)
    {
        this.productDataStore.setProductShapeDimension(dimensionName, dimensionValue);
    }

    validateShapeDimension(dimensionName: string, dimensionValue: number): number
    {
        const heightParameters = ['h1', 'h2', 'h3'];
        const widthParameters = ['s1', 's2', 's3'];
        const maximumWidth = this.productDataService.getMaximumProductWidth();
        const minimumWidth = this.productDataService.getMinimumProductWidth();
        const maximumHeight = this.productDataService.getMaximumProductHeight();
        const minimumHeight = this.productDataService.getMinimumProductHeight();

        const shape = this.getShape();

        if (widthParameters.includes(dimensionName)) {
            /*
            in this case we just want to sum provided value with other existing values
            the idea is to first sum all existing values then subtract provided one and add provided value at the end
            this one tricky line prevents us to write some filter methods to find proper shape values
            */
            const newTotalWidth = (shape.s1 + shape.s2 + shape.s3) - shape[dimensionName] + dimensionValue;
            if (newTotalWidth > maximumWidth || newTotalWidth < minimumWidth) {
                return shape[dimensionName];
            }
        }

        if (heightParameters.includes(dimensionName)) {
            const maxHeight = Math.max(shape.h1, shape.h2, shape.h3, dimensionValue);
            if (maxHeight > maximumHeight || maxHeight < minimumHeight) {
                return shape[dimensionName];
            }
            const shapeNewHeightValues = {};
            for (let heightParameter of heightParameters) {
                shapeNewHeightValues[heightParameter] = shape[heightParameter];
            }
            shapeNewHeightValues[dimensionName] = dimensionValue;

            if (shapeNewHeightValues['h1'] < shapeNewHeightValues['h2'] || shapeNewHeightValues['h1'] < shapeNewHeightValues['h3']) {
                return shape[dimensionName];
            }
        }
        return dimensionValue;
    }

    getShapeWidth()
    {
        const shape = this.getShape();
        return (shape.s1 + shape.s2 + shape.s3);
    }

    getMaximumShapeHeight()
    {
        const shape = this.getShape();
        return Math.max(shape.h1, shape.h2, shape.h3);
    }

    getShape()
    {
        return this.productDataStore.getProductShapeConfiguration();
    }

    setZeroSizesForPredefinedPolygonShape(shapeType) {
        switch (shapeType) {
            case 'SLT':
                this.productDataStore.setProductShapeDimension('s2', 0);
                this.productDataStore.setProductShapeDimension('s3', 0);
                this.productDataStore.setProductShapeDimension('h2', 0);
                break;
            case 'SRT':
                this.productDataStore.setProductShapeDimension('s1', 0);
                this.productDataStore.setProductShapeDimension('s2', 0);
                this.productDataStore.setProductShapeDimension('h3', 0);
                break;
            case 'SLS':
                this.productDataStore.setProductShapeDimension('s3', 0);
                this.productDataStore.setProductShapeDimension('h2', 0);
                this.productDataStore.setProductShapeDimension('h3', 0);
                break;
            case 'SRS':
                this.productDataStore.setProductShapeDimension('s1', 0);
                this.productDataStore.setProductShapeDimension('h2', 0);
                this.productDataStore.setProductShapeDimension('h3', 0);
                break;
            case 'SLC':
                this.productDataStore.setProductShapeDimension('s3', 0);
                this.productDataStore.setProductShapeDimension('h2', 0);
                break;
            case 'SRC':
                this.productDataStore.setProductShapeDimension('s1', 0);
                this.productDataStore.setProductShapeDimension('h3', 0);
                break;
            case 'DLT':
                this.productDataStore.setProductShapeDimension('s2', 0);
                break;
            case 'DLS':
                this.productDataStore.setProductShapeDimension('h2', 0);
                this.productDataStore.setProductShapeDimension('h3', 0);
                break;
        }
    }

    setPredefinedPolygonShape(shapeType) {
        const width = this.productDataStore.getAttributeByName('width');
        const height = this.productDataStore.getAttributeByName('height');

        switch (shapeType) {
            case 'SLT':
                this.productDataStore.setProductShapeDimension('s1', width);
                this.productDataStore.setProductShapeDimension('s2', 0);
                this.productDataStore.setProductShapeDimension('s3', 0);
                this.productDataStore.setProductShapeDimension('h1', height);
                this.productDataStore.setProductShapeDimension('h2', 0);
                this.productDataStore.setProductShapeDimension('h3', Math.ceil(height * 0.5));
                break;

            case 'SRT':
                this.productDataStore.setProductShapeDimension('s1', 0);
                this.productDataStore.setProductShapeDimension('s2', 0);
                this.productDataStore.setProductShapeDimension('s3', width);
                this.productDataStore.setProductShapeDimension('h1', height);
                this.productDataStore.setProductShapeDimension('h2', Math.ceil(height * 0.5));
                this.productDataStore.setProductShapeDimension('h3', 0);
                break;

            case 'SLS':
                this.productDataStore.setProductShapeDimension('s1', Math.ceil(width * 0.3));
                this.productDataStore.setProductShapeDimension('s2', width - this.productDataStore.getProductShapeDimension('s1'));
                this.productDataStore.setProductShapeDimension('s3', 0);
                this.productDataStore.setProductShapeDimension('h1', height);
                this.productDataStore.setProductShapeDimension('h2', 0);
                this.productDataStore.setProductShapeDimension('h3', 0);
                break;

            case 'SRS':
                this.productDataStore.setProductShapeDimension('s1', 0);
                this.productDataStore.setProductShapeDimension('s3', Math.ceil(width * 0.3));
                this.productDataStore.setProductShapeDimension('s2', width - this.productDataStore.getProductShapeDimension('s3'));
                this.productDataStore.setProductShapeDimension('h1', height);
                this.productDataStore.setProductShapeDimension('h2', 0);
                this.productDataStore.setProductShapeDimension('h3', 0);

                break;

            case 'SLC':
                this.productDataStore.setProductShapeDimension('s1', Math.ceil(width * 0.7));
                this.productDataStore.setProductShapeDimension('s2', width - this.productDataStore.getProductShapeDimension('s1'));
                this.productDataStore.setProductShapeDimension('s3', 0);
                this.productDataStore.setProductShapeDimension('h1', height);
                this.productDataStore.setProductShapeDimension('h2', 0);
                this.productDataStore.setProductShapeDimension('h3', Math.ceil(height * 0.5));
                break;

            case 'SRC':
                this.productDataStore.setProductShapeDimension('s1', 0);
                this.productDataStore.setProductShapeDimension('s3', Math.ceil(width * 0.7));
                this.productDataStore.setProductShapeDimension('s2', width - this.productDataStore.getProductShapeDimension('s3'));
                this.productDataStore.setProductShapeDimension('h1', height);
                this.productDataStore.setProductShapeDimension('h2', Math.ceil(height * 0.5));
                this.productDataStore.setProductShapeDimension('h3', 0);
                break;

            case 'DLT':
                this.productDataStore.setProductShapeDimension('s1', Math.ceil(width * 0.5));
                this.productDataStore.setProductShapeDimension('s2', 0);
                this.productDataStore.setProductShapeDimension('s3', Math.ceil(width * 0.5));
                this.productDataStore.setProductShapeDimension('h1', height);
                this.productDataStore.setProductShapeDimension('h2', Math.ceil(height * 0.5));
                this.productDataStore.setProductShapeDimension('h3', Math.ceil(height * 0.5));
                break;

            case 'DLS':
                this.productDataStore.setProductShapeDimension('s1',  Math.ceil(width * 0.2));
                this.productDataStore.setProductShapeDimension('s2', Math.ceil(width - width * 0.4));
                this.productDataStore.setProductShapeDimension('s3',  Math.ceil(width * 0.2));
                this.productDataStore.setProductShapeDimension('h1', height);
                this.productDataStore.setProductShapeDimension('h2', 0);
                this.productDataStore.setProductShapeDimension('h3', 0);

                break;

            case 'DLC':
                this.productDataStore.setProductShapeDimension('s1',  Math.ceil(width * 0.3));
                this.productDataStore.setProductShapeDimension('s2', Math.ceil(width - width * 0.6));
                this.productDataStore.setProductShapeDimension('s3',  Math.ceil(width * 0.3));
                this.productDataStore.setProductShapeDimension('h1', height);
                this.productDataStore.setProductShapeDimension('h2', Math.ceil(height * 0.5));
                this.productDataStore.setProductShapeDimension('h3', Math.ceil(height * 0.5));
                break;
        }
    }
}
